import {
  BasicSelectOption,
  BeeFontData,
  //app format types
  AppThemeData,
  AppFontsFormData,
  AppTypographyFormData,
  AppButtonFormData,
  AppLayoutFormData,
  //API format types
  APIThemeData,
  APIFormatThemeFontsData,
  APIFormatThemeTypographyData,
  APIFormatThemeButtonData,
  APIFormatThemeLayoutData,
  CustomSelectOption,
  APIFormatBaseTypographyInput,
} from "containers/admin/clients/client/theme-page/preview/preview.types";

import { COLOR_NONE } from "containers/admin/clients/client/theme-page/preview/preview.constants";
import { APP_WEIGHT_LABELS } from "containers/admin/clients/client/theme-page/preview/sub-tabs/fonts/fonts.constants";
import { FALLBACK_FONT_FAMILY_OPTIONS } from "containers/admin/clients/client/theme-page/preview/sub-tabs/typography/typography.constants";

//transform data to app format theme data
const getLabelForTextColor = (value: string): string => {
  const map: Record<string, string> = {
    primary: "Brand/Primary",
    secondary: "Brand/Secondary",
    tertiary: "Brand/Tertiary",
    intense: "Neutrals/Intense",
    bold: "Neutrals/Bold",
    soft: "Neutrals/Soft",
    subtle: "Neutrals/Subtle",
    white: "Neutrals/White",
  };
  return map[value] || "Custom";
};

const getLabelForFallbackFont = (value: string): string => {
  const option = FALLBACK_FONT_FAMILY_OPTIONS.find((opt) => opt.value === value);
  return option?.label || value;
};

const getLabelForFontWeight = (value: string): string => {
  if (!value) return "Select...";

  return APP_WEIGHT_LABELS[value] || value;
};

const getSelectOption = (value: string): BasicSelectOption => {
  return {
    label: value.charAt(0).toUpperCase() + value.substring(1),
    value: value,
  };
};

const checkIfColorCustom = (colorValue: string, colorsKeys: string[]) => {
  return (
    !colorsKeys.find((color) => color === colorValue.toLowerCase()) &&
    colorValue.toLowerCase() !== "none"
  );
};

const getColorOption = (colorValue: string, isColorCustom: boolean): string => {
  const option = isColorCustom ? "" : colorValue;

  return getLabelForTextColor(option);
};

const getColorNameObject = (color: string, isColorCustom: boolean): CustomSelectOption => {
  return color === "none"
    ? COLOR_NONE
    : {
        label: getColorOption(color, isColorCustom),
        value: isColorCustom ? "custom" : color,
      };
};

export const transformAPIFontsData = (fonts: APIFormatThemeFontsData): AppFontsFormData => {
  return {
    adobeFontsProjectId: fonts.adobeFontsProjectId,
    googleFonts: fonts?.googleFonts?.length //if we have saved googleFonts
      ? //we set them
        fonts?.googleFonts.map((family: string) => {
          return {
            value: family,
            label: family,
            appliedToPreview: true,
          };
        })
      : //otherwise - we set one default object that will become an empty value for select
        [
          {
            value: null,
            label: null,
            appliedToPreview: false,
          },
        ],
  };
};

const getFontValue = (value: string, fonts: BeeFontData[]): BasicSelectOption => {
  if (!value) return { label: "Select...", value: "" };

  const currentFont = fonts.find((font) => font.value === value);

  return { label: currentFont?.name as string, value: currentFont?.value as string };
};

const getFallbackFontValue = (value: string): BasicSelectOption => {
  if (!value) return { label: "Select...", value: "" };

  const label = getLabelForFallbackFont(value);

  return { label, value };
};

const getButtonWidthName = (value: string): BasicSelectOption => {
  switch (value) {
    case "":
    case "auto":
      return { label: "Hug content", value: "auto" };
    case "100":
      return { label: "Fill container", value: "fill" };
    default:
      return { label: "Fixed width", value: "fixed" };
  }
};

const getButtonWidthCustom = (value: string): string => {
  switch (value) {
    case "":
    case "100":
    case "auto":
      return "50";
    default:
      return value;
  }
};

// Maps current lineHeight value to accepted options for backwards compatibility with old field
// Can be cleaned up once all themes are updated
const roundLineHeight = (lineHeight: string): { label: string; value: string } => {
  const numValue = Number.parseFloat(lineHeight);

  if (Number.isNaN(numValue) || numValue < 1.4) {
    return { label: "1.2", value: "1.2" };
  }
  if (numValue < 1.7) return { label: "1.5", value: "1.5" };
  if (numValue < 1.9) return { label: "1.8", value: "1.8" };

  return { label: "2.0", value: "2.0" };
};

const getBaseTypographyData = (
  data: APIFormatBaseTypographyInput,
  colorsKeys: string[],
  fonts: BeeFontData[],
) => ({
  fontFamily: getFontValue(data.fontFamily, fonts),
  fallbackFontFamily: getFallbackFontValue(data.fallbackFontFamily),
  fontSize: { label: data.fontSize, value: data.fontSize },
  lineHeight: roundLineHeight(data.lineHeight),
  fontWeight: {
    label: getLabelForFontWeight(data.fontWeight),
    value: data.fontWeight,
  },
  textColor: {
    label: getColorOption(data.textColor, checkIfColorCustom(data.textColor, colorsKeys)),
    value: checkIfColorCustom(data.textColor, colorsKeys) ? "custom" : data.textColor,
  },
  customColor: checkIfColorCustom(data.textColor, colorsKeys) ? data.textColor : "",
});

const getBasePaddingData = (padding: {
  left: string;
  right: string;
  top: string;
  bottom: string;
}) => {
  const left = padding.left;
  const right = padding.right;
  const top = padding.top;
  const bottom = padding.bottom;

  // Calculate isCustom based on whether left/right and top/bottom match
  const isXDifferent = left !== right;
  const isYDifferent = top !== bottom;
  const isCustom = isXDifferent || isYDifferent;

  return {
    left,
    right,
    top,
    bottom,
    isCustom,
  };
};

const getResponsivePaddingData = (responsivePadding: {
  mobile: { left: string; right: string; top: string; bottom: string };
  desktop: { left: string; right: string; top: string; bottom: string };
}) => ({
  mobile: getBasePaddingData(responsivePadding.mobile),
  desktop: getBasePaddingData(responsivePadding.desktop),
});

const getGroupOption = (value: string): CustomSelectOption => {
  const groupOptions: Record<string, string> = {
    "all-blocks": "All content blocks",
    custom: "Custom",
  };

  return {
    label: groupOptions[value] || "All content blocks",
    value: value || "all-blocks",
  };
};

export const transformAPITypographyData = (
  typography: APIFormatThemeTypographyData,
  colorsKeys: string[],
  fonts: BeeFontData[],
): AppTypographyFormData => {
  const typographyData: AppTypographyFormData = {
    heading: {
      ...getBaseTypographyData(typography.heading, colorsKeys, fonts),
      alignment: typography.heading.alignment,
    },
    subHeading: {
      ...getBaseTypographyData(typography.subHeading, colorsKeys, fonts),
      alignment: typography.subHeading.alignment,
    },
    paragraph: {
      ...getBaseTypographyData(typography.paragraph, colorsKeys, fonts),
      alignment: typography.paragraph.alignment,
      linkColor: {
        label: getColorOption(
          typography.paragraph.linkColor || "",
          typography.paragraph.linkColor?.startsWith("#") || false,
        ),
        value: typography.paragraph.linkColor?.startsWith("#")
          ? "custom"
          : typography.paragraph.linkColor || "",
      },
      customLinkColor: typography.paragraph.linkColor?.startsWith("#")
        ? typography.paragraph.linkColor
        : "",
    },
    buttonLabel: getBaseTypographyData(typography.buttonLabel, colorsKeys, fonts),
  };

  return typographyData;
};

export const transformAPIButtonData = (
  button: APIFormatThemeButtonData,
  colorsKeys: string[],
): AppButtonFormData => {
  const isBgColorCustom = checkIfColorCustom(button.backgroundColor, colorsKeys);
  const isBorderStyleColorCustom = checkIfColorCustom(button.border.color, colorsKeys);

  return {
    padding: getBasePaddingData(button.padding),
    width: {
      name: getButtonWidthName(button.width),
      custom: getButtonWidthCustom(button.width),
    },
    alignment: button.alignment,
    backgroundColor: {
      name: getColorNameObject(button.backgroundColor, isBgColorCustom),
      custom: isBgColorCustom ? button.backgroundColor : "",
    },
    borderRadius: {
      name: {
        // 60px border should have label "Rounded"
        label: button.border.radius === "60px" ? "Rounded" : button.border.radius,
        value: button.border.radius,
      },
      custom: button.border.radius === "custom" ? button.border.radius : "",
    },
    borderStyle: {
      name: getSelectOption(button.border.style),
      width: button.border.width,
      color: getColorNameObject(button.border.color, isBorderStyleColorCustom),
      custom: isBorderStyleColorCustom ? button.border.color : "",
    },
  };
};

export const transformAPILayoutData = (layout: APIFormatThemeLayoutData): AppLayoutFormData => {
  return {
    contentArea: getBasePaddingData(layout.contentArea),
    contentBlock: {
      group: getGroupOption(layout.contentBlock.group),
      allBlocks: getResponsivePaddingData(layout.contentBlock.allBlocks),
      headings: getResponsivePaddingData(layout.contentBlock.headings),
      paragraph: getResponsivePaddingData(layout.contentBlock.paragraph),
      button: getResponsivePaddingData(layout.contentBlock.button),
    },
  };
};

export const transformThemeDataOnGet = (inputData: APIThemeData): AppThemeData => {
  const colorsKeys = Object.keys(inputData.colors);

  const colors = {
    ...inputData.colors,
  };

  const fonts = transformAPIFontsData(inputData.fonts);

  const typography = transformAPITypographyData(
    inputData.typography,
    colorsKeys,
    inputData.fonts?.beeFonts as BeeFontData[],
  );

  const button = transformAPIButtonData(inputData.button, colorsKeys);

  const banner = {
    ...inputData.banner,
  };

  const layout = transformAPILayoutData(inputData.layout);

  return { colors, fonts, typography, button, banner, layout };
};
